import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Sparkles, ArrowLeft, Download, Heart, Brain, MessageSquare,
  Briefcase, Target, BookOpen, Users, Globe, Shield, Volume2,
  Calendar, Fingerprint, Zap, Crown, ExternalLink, Copy
} from "lucide-react";
import { useState } from "react";

const SCREENSHOTS = [
  {
    title: "AI Chat Interface",
    desc: "Natural conversation with 6 specialized AI guides — Friend, Mentor, Social Guide, and more.",
    url: "https://static.prod-images.emergentagent.com/jobs/f365b91f-3172-4460-a6c3-f2d4f393d18f/images/c9d42a34b95404b0f1383a89e6d248db68d16f82ac41f1ce3af2576e75889442.jpeg",
  },
  {
    title: "Mood Journal & Tracking",
    desc: "Log emotions daily with heatmap visualizations, weekly AI insights, and mood-aware responses.",
    url: "https://static.prod-images.emergentagent.com/jobs/f365b91f-3172-4460-a6c3-f2d4f393d18f/images/cc3d29aa880ab0928fa1631683cdc170a0cacd8e1f933660df0f7d2823ae2ca2.jpeg",
  },
  {
    title: "Admin Operations Dashboard",
    desc: "Real-time platform metrics — user stats, subscription tiers, module usage, and 7-day activity.",
    url: "https://static.prod-images.emergentagent.com/jobs/f365b91f-3172-4460-a6c3-f2d4f393d18f/images/d082e8daa1cba805788a3245d9dee22699cd6a26a72b0dc11ed90d09ac6b290f.jpeg",
  },
  {
    title: "Mobile Experience",
    desc: "Calendar, settings, and smart suggestions — optimized for mobile with PWA and native app support.",
    url: "https://static.prod-images.emergentagent.com/jobs/f365b91f-3172-4460-a6c3-f2d4f393d18f/images/1e1ab583539b54bfb5cba8d396f6c6b796fe131ea502eecbaa062b91b4ae64d3.jpeg",
  },
];

const PRODUCT_INFO = {
  name: "Omni",
  tagline: "The AI companion that truly knows you.",
  oneLiner: "An AI life companion with 7 specialized guides, 22 Wisdom Council mentors, and 113-language support — designed to remember, adapt, and grow with you.",
  founded: "2026",
  category: "AI / Mental Wellness / Personal Development",
  platforms: ["Web (PWA)", "iOS (Coming Soon)", "Android (Coming Soon)"],
  pricing: "Free to start. Plans from $9.99/mo.",
};

const TEAM = [
  {
    name: "Moyo Oyetunde",
    role: "Founder & CEO",
    bio: "Visionary behind Omni's mission to make culturally intelligent AI accessible to everyone. Passionate about bridging technology and human connection across languages and traditions.",
    initials: "MO",
  },
];

const KEY_FEATURES = [
  { icon: Heart, title: "6 AI Guides", desc: "Friend, Mentor, Social Guide, Job Guardian, Life Coach, Storyteller" },
  { icon: Users, title: "22 Wisdom Council Mentors", desc: "Rumi, Cleopatra, Marcus Aurelius, Maya Angelou, Confucius, and 17 more" },
  { icon: Globe, title: "113 Languages", desc: "6 free languages + 107 premium with Global Voice Pack" },
  { icon: Brain, title: "AI Memory", desc: "Remembers preferences, history, and context across sessions" },
  { icon: Volume2, title: "Voice Conversations", desc: "Natural TTS + STT — speak and listen hands-free" },
  { icon: Calendar, title: "Mood & Goal Tracking", desc: "Heatmaps, goals, streaks, and daily recaps" },
  { icon: Zap, title: "Cultural Awareness", desc: "Heritage-sensitive AI with smart greetings in native languages" },
  { icon: Shield, title: "Privacy-First", desc: "Encrypted storage, biometric login, GDPR compliant" },
];

const BRAND_COLORS = [
  { name: "Omni Red", hex: "#DC2626", rgb: "220, 38, 38" },
  { name: "Dark Background", hex: "#060912", rgb: "6, 9, 18" },
  { name: "Card Dark", hex: "#0A0D16", rgb: "10, 13, 22" },
  { name: "Text Primary", hex: "#FFFFFF", rgb: "255, 255, 255" },
  { name: "Text Muted", hex: "#71717A", rgb: "113, 113, 122" },
  { name: "Accent Gold", hex: "#E8A817", rgb: "232, 168, 23" },
];

const LOGOS = [
  { name: "Logo Dark (SVG)", desc: "For dark backgrounds", type: "svg" },
  { name: "Logo Light (SVG)", desc: "For light backgrounds", type: "svg" },
  { name: "Icon Only (PNG)", desc: "Square app icon", type: "png" },
  { name: "Full Wordmark (PNG)", desc: "Logo + text", type: "png" },
];

const MILESTONES = [
  { date: "2026", label: "Omni conceived — vision for culturally intelligent AI companion" },
  { date: "Q1 2026", label: "Core platform built — 7 AI guides, mood tracking, goals" },
  { date: "Q2 2026", label: "Wisdom Council launched — 22 historical mentors with unique voices" },
  { date: "Q3 2026", label: "Global launch — 113 languages, Stripe live, PWA + native mobile" },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="text-zinc-600 hover:text-white transition-colors" title="Copy">
      <Copy className="w-3.5 h-3.5" />
      {copied && <span className="text-[10px] text-emerald-400 ml-1">Copied</span>}
    </button>
  );
}

function Section({ id, label, title, children, className }) {
  return (
    <section id={id} className={cn("py-16 md:py-20", className)}>
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-3">{label}</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-8">{title}</h2>
      {children}
    </section>
  );
}

export default function PressKitPage() {
  return (
    <div className="min-h-screen" style={{ background: "#060912", color: "#fff" }}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b" style={{ background: "rgba(6,9,18,0.85)", borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(220,38,38,0.12)" }}>
              <Sparkles className="w-4 h-4 text-red-500" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-base font-medium tracking-tight">Omni</span>
            <span className="text-zinc-600 mx-1">/</span>
            <span className="text-sm text-zinc-400">Press Kit</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <a href="#screenshots" className="hover:text-white transition-colors">Screenshots</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#team" className="hover:text-white transition-colors">Team</a>
            <a href="#brand" className="hover:text-white transition-colors">Brand</a>
            <a href="#logos" className="hover:text-white transition-colors">Logos</a>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 pt-28">
        {/* Hero */}
        <div className="pb-16 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-5">Press Kit</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tighter leading-tight mb-6">
            Everything you need<br />to tell the Omni story
          </h1>
          <p className="text-zinc-400 max-w-2xl leading-relaxed mb-8">
            Logos, brand guidelines, product screenshots, team bios, and key facts — all in one place for journalists, bloggers, and partners.
          </p>
          <Link to="/">
            <span className="text-sm text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Omni
            </span>
          </Link>
        </div>

        {/* About */}
        <Section id="about" label="About" title="What is Omni?">
          <div className="rounded-2xl p-7 border mb-6" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
            <p className="text-base text-zinc-300 leading-relaxed mb-4">{PRODUCT_INFO.oneLiner}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div>
                <p className="text-[11px] text-zinc-600 uppercase tracking-widest mb-1">Founded</p>
                <p className="text-sm font-medium">{PRODUCT_INFO.founded}</p>
              </div>
              <div>
                <p className="text-[11px] text-zinc-600 uppercase tracking-widest mb-1">Category</p>
                <p className="text-sm font-medium">{PRODUCT_INFO.category}</p>
              </div>
              <div>
                <p className="text-[11px] text-zinc-600 uppercase tracking-widest mb-1">Pricing</p>
                <p className="text-sm font-medium">{PRODUCT_INFO.pricing}</p>
              </div>
              <div>
                <p className="text-[11px] text-zinc-600 uppercase tracking-widest mb-1">Platforms</p>
                <p className="text-sm font-medium">{PRODUCT_INFO.platforms.join(", ")}</p>
              </div>
            </div>
          </div>

          {/* Boilerplate */}
          <div className="rounded-2xl p-7 border" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Boilerplate (Copy-Ready)</p>
              <CopyButton text="Omni is the world's first culturally intelligent AI life companion. With 6 specialized AI guides, 22 historical Wisdom Council mentors, mood tracking, and voice conversations in 113 languages, Omni offers a deeply personal AI experience for individuals, families, and organizations. Free to start at omni-app.com." />
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Omni is the world's first culturally intelligent AI life companion. With 6 specialized AI guides, 22 historical Wisdom Council mentors, mood tracking, and voice conversations in 113 languages, Omni offers a deeply personal AI experience for individuals, families, and organizations. Free to start at omni-app.com.
            </p>
          </div>
        </Section>

        {/* Screenshots Gallery */}
        <Section id="screenshots" label="Product" title="Screenshots">
          <p className="text-sm text-zinc-400 mb-6">
            High-resolution product mockups for editorial use. Right-click to save or download.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SCREENSHOTS.map(s => (
              <div key={s.title} data-testid={`press-screenshot-${s.title.toLowerCase().replace(/\s/g,'-')}`} className="rounded-xl border overflow-hidden group" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                <div className="relative overflow-hidden">
                  <img src={s.url} alt={s.title} className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" />
                </div>
                <div className="p-4" style={{ background: "rgba(255,255,255,0.02)" }}>
                  <p className="text-sm font-medium mb-1">{s.title}</p>
                  <p className="text-xs text-zinc-500">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Key Features */}
        <Section id="features" label="Features" title="What makes Omni different">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {KEY_FEATURES.map(f => (
              <div key={f.title} className="rounded-xl p-5 border flex gap-4" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(220,38,38,0.08)" }}>
                  <f.icon className="w-5 h-5 text-red-500/80" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">{f.title}</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Timeline */}
        <Section id="timeline" label="Journey" title="Key milestones">
          <div className="space-y-0 relative ml-4">
            <div className="absolute left-0 top-2 bottom-2 w-px bg-zinc-800" />
            {MILESTONES.map((m, i) => (
              <div key={i} className="relative pl-8 pb-8 last:pb-0">
                <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-red-500 -translate-x-[3.5px]" />
                <p className="text-xs text-red-500 font-medium mb-1">{m.date}</p>
                <p className="text-sm text-zinc-400">{m.label}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Team */}
        <Section id="team" label="Team" title="The people behind Omni">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TEAM.map(person => (
              <div key={person.name} className="rounded-2xl p-7 border" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-lg font-medium" style={{ background: "rgba(220,38,38,0.12)", color: "#ef4444" }}>
                    {person.initials}
                  </div>
                  <div>
                    <p className="font-heading text-base font-medium">{person.name}</p>
                    <p className="text-xs text-zinc-500">{person.role}</p>
                  </div>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{person.bio}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Brand Colors */}
        <Section id="brand" label="Brand" title="Colors & typography">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
            {BRAND_COLORS.map(c => (
              <div key={c.name} className="rounded-xl border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                <div className="h-16" style={{ background: c.hex }} />
                <div className="p-3" style={{ background: "rgba(255,255,255,0.02)" }}>
                  <p className="text-xs font-medium mb-0.5">{c.name}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-[11px] text-zinc-500">{c.hex}</p>
                    <CopyButton text={c.hex} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl p-6 border" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Typography</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="font-heading text-2xl font-light mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Outfit</p>
                <p className="text-xs text-zinc-500">Headings — Light 300 / Regular 400 / Medium 500</p>
              </div>
              <div>
                <p className="text-2xl font-light mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Playfair Display</p>
                <p className="text-xs text-zinc-500">Wisdom Council names — Regular 400 / Italic</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Logo Downloads */}
        <Section id="logos" label="Assets" title="Logo & icon downloads">
          <p className="text-sm text-zinc-400 mb-6">
            Please use the Omni logo as provided. Do not alter colors, proportions, or add effects. Maintain clear space equal to the height of the icon on all sides.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {LOGOS.map(logo => (
              <div key={logo.name} className="rounded-xl p-5 border flex items-center justify-between" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: logo.name.includes("Light") ? "#fff" : "rgba(220,38,38,0.12)" }}>
                    <Sparkles className={cn("w-5 h-5", logo.name.includes("Light") ? "text-red-600" : "text-red-500")} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{logo.name}</p>
                    <p className="text-[11px] text-zinc-600">{logo.desc}</p>
                  </div>
                </div>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider border rounded-full px-2 py-0.5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                  .{logo.type}
                </span>
              </div>
            ))}
          </div>

          {/* Inline Logo Preview */}
          <div className="rounded-2xl p-8 border text-center" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
            <p className="text-xs text-zinc-600 uppercase tracking-widest mb-6">Logo Preview</p>
            <div className="flex items-center justify-center gap-12 flex-wrap">
              {/* Dark bg version */}
              <div className="flex flex-col items-center gap-3">
                <div className="w-24 h-24 rounded-2xl flex items-center justify-center" style={{ background: "#060912", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(220,38,38,0.12)" }}>
                      <Sparkles className="w-4.5 h-4.5 text-red-500" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-zinc-600">Dark</p>
              </div>
              {/* Light bg version */}
              <div className="flex flex-col items-center gap-3">
                <div className="w-24 h-24 rounded-2xl flex items-center justify-center bg-white">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(220,38,38,0.08)" }}>
                      <Sparkles className="w-4.5 h-4.5 text-red-600" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-zinc-600">Light</p>
              </div>
              {/* Wordmark */}
              <div className="flex flex-col items-center gap-3">
                <div className="h-24 px-8 rounded-2xl flex items-center justify-center" style={{ background: "#060912", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(220,38,38,0.12)" }}>
                      <Sparkles className="w-4.5 h-4.5 text-red-500" strokeWidth={1.5} />
                    </div>
                    <span className="font-heading text-xl font-medium tracking-tight text-white">Omni</span>
                  </div>
                </div>
                <p className="text-[10px] text-zinc-600">Wordmark</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Contact */}
        <section className="py-16 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-3">Contact</p>
            <h2 className="font-heading text-2xl font-light tracking-tight mb-4">Media inquiries</h2>
            <p className="text-sm text-zinc-400 mb-6">
              For press inquiries, interviews, or partnership opportunities, reach out to the Omni team.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/" className="text-sm text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Omni
              </Link>
              <Link to="/status" className="text-sm text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1">
                System Status <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600&family=Playfair+Display:ital,wght@0,400;0,600;1,400&display=swap');
        .font-heading { font-family: 'Outfit', sans-serif; }
      `}</style>
    </div>
  );
}
